/**
 * @file     MatchCards.tsx
 * @purpose  The two deterministic matches shown with an assistant turn: reason
 *           chips (why these fit) above the existing PropertyCard. Desktop:
 *           side by side; mobile: horizontal swipe.
 */
'use client';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { reltaTrack } from '@/hooks/useReltaTrack';
import { ChatMatchCard } from './ChatMatchCard';
import { ChatShowingCard } from './ChatShowingCard';
import type { MlsListingCardData } from '@/types/listing';

export function MatchCards({
  matches,
  reasons = [],
  onOpenListing,
}: {
  matches: MlsListingCardData[];
  reasons?: string[];
  onOpenListing?: (listing: MlsListingCardData) => void;
}) {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  if (matches.length === 0) return null;

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {reasons.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
          <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: theme.semanticColors.textSecondary, mr: 0.5 }}>
            {t('aiChat.matchedOn', 'Matched on')}
          </Box>
          {reasons.map((r) => (
            <Chip
              key={r}
              label={r}
              size="small"
              sx={{
                borderRadius: 1,
                fontSize: 11,
                height: 22,
                backgroundColor: 'transparent',
                border: `1px solid ${theme.semanticColors.borderSubtle}`,
                color: theme.semanticColors.textSecondary,
              }}
            />
          ))}
        </Box>
      )}
      {matches.length === 1 ? (
        // The showing: one home presented rich, evaluable without leaving chat.
        <ChatShowingCard listing={matches[0]} onOpenListing={onOpenListing} />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          {matches.map((m) => (
            <ChatMatchCard
              key={m.listingKey}
              listing={m}
              onOpen={() => reltaTrack('ai_chat.match_focus', { listing_key: m.listingKey })}
              onOpenListing={onOpenListing}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
